/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import type {
    GetStaticPaths,
    GetStaticPropsContext,
    InferGetStaticPropsType,
    NextPage,
    PreviewData,
} from "next";
import Head from "next/head";
import { ParsedUrlQuery } from "querystring";
import { useMemo } from "react";

import { StrapiTerminusTopic } from "@/types/strapi";

import { Spacing } from "@/tokens/spacing";

import { Divider } from "@/ui/atoms/divider";
import { GridColumn } from "@/ui/atoms/grid_column";
import { Icon } from "@/ui/atoms/icon";
import { Link } from "@/ui/atoms/link";
import { SubgridContainer } from "@/ui/atoms/subgrid_container";
import { TerminusAuthorChip } from "@/ui/atoms/terminus_author_chip";
import { Text } from "@/ui/atoms/text";
import { ArticleBody } from "@/ui/organisms/article_body";
import { TerminusTopicChipList } from "@/ui/organisms/terminus_topic_chip_list";
import { TerminusLayout } from "@/ui/wrappers/terminus_layout";

import { generatePageLevelProps } from "@/util/data_util";
import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { generateLegacyPreFooterData } from "@/util/strapi_data_util";
import {
    getGlobals,
    getTerminusArticleSlugPage,
    getTerminusArticleSlugs,
    getTerminusGlobals,
    getTerminusTopics,
} from "@/util/strapi_util";
import { buildStylesByBreakpoint } from "@/util/style_util";

export const getStaticProps = async ({
    params,
    preview: previewMode,
}: GetStaticPropsContext<ParsedUrlQuery, PreviewData>) => {
    const slug = params?.slug as string | undefined;

    if (!slug) {
        return {
            notFound: true,
        };
    }

    const globalsData = await getGlobals(previewMode);
    const terminusGlobalsData = await getTerminusGlobals(previewMode);

    const terminusTopicsData = await getTerminusTopics(
        {
            featureArticleLimit: 0,
            relatedArticleLimit: 12,
            relatedArticleSlugToExclude: slug,
        },
        previewMode,
    );

    const pageData = await getTerminusArticleSlugPage(slug, previewMode);

    if (!pageData) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            ...generatePageLevelProps({
                path: `/terminus/${slug}`,
                previewMode,
                slug,
            }),
            ...generateLegacyPreFooterData(globalsData),
            globalsData,
            legacyGrid: false,
            pageData,
            terminusGlobalsData,
            terminusTopicsData,
        },
    };
};

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getTerminusArticleSlugs();

    return {
        fallback: false,
        paths: slugs.map((slug) => ({ params: { slug } })),
    };
};

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Page: NextPage<PageProps> = (props) => {
    /**
     * Globals
     */
    const { pageData, terminusGlobalsData, terminusTopicsData } = props;

    const articleTopics = pageData.Topics?.map((t) => t.Slug);

    const currentTopic = terminusTopicsData.find((topic) => {
        return articleTopics?.includes(topic.Slug);
    });

    const { Related_Articles } = currentTopic as StrapiTerminusTopic;

    /**
     * Metadata
     */
    const structuredData = useMemo(
        () =>
            JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                authors: pageData.Authors?.map((author) => ({
                    "@type": "Person",
                    description: author.Bio,
                    name: author.Name,
                })),
                dateModified: pageData.Update_Date,
                datePublished: pageData.Publish_Date,
                description: pageData.Description,
                name: pageData.Title,
            }),
        [pageData],
    );

    /**
     * Theme
     */
    const theme = useTypedTheme();

    /**
     * Styles
     */
    const terminusAuthorChipContainerStyle = css({
        alignItems: "center",
        columnGap: Spacing["spacing-4"],
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        marginBottom: Spacing["spacing-6"],
        marginTop: Spacing["spacing-8"],
        rowGap: Spacing["spacing-2"],
    });

    const terminusAuthorChipStyles = css(
        buildStylesByBreakpoint("marginTop", {
            extraSmall: Spacing["spacing-4"],
            large: Spacing["spacing-8"],
        }),
        buildStylesByBreakpoint("marginBottom", {
            extraSmall: Spacing["spacing-8"],
            large: Spacing["spacing-12"],
        }),
    );

    const terminusPublishDateStyles = css(
        {
            alignSelf: pageData.Update_Date ? undefined : "flex-start",
            display: "flex",
            flexDirection: "column",
            gap: Spacing["spacing-half"],
        },
        buildStylesByBreakpoint("alignItems", {
            extraSmall: "flex-start",
            small: "flex-end",
        }),
    );

    /**
     * Rendering
     */

    const renderTerminusArticleHeader = () => {
        const authors = pageData.Authors;
        const topics = pageData.Topics;

        return (
            <GridColumn
                columnSpan={{
                    extraSmall: "full",
                    medium: 6,
                    large: 4,
                }}
                columnStart={{
                    extraSmall: 1,
                    large: 2,
                }}
            >
                <Text
                    fontSize="TerminusArticleTitle"
                    shouldBalanceWrap={false}
                    tag="h1"
                >
                    {pageData.Title}
                </Text>

                {authors?.length && (
                    <div css={terminusAuthorChipContainerStyle}>
                        <TerminusAuthorChip
                            author={authors[0]}
                            showCompanyAndRole={true}
                        />

                        <div css={terminusPublishDateStyles}>
                            {pageData.Update_Date && (
                                <Text
                                    fontSize="TerminusPublishedDate"
                                    themeKey="textSecondary"
                                >
                                    {`Updated: ${new Date(`${pageData.Update_Date}T00:00:00`).toLocaleDateString()}`}
                                </Text>
                            )}

                            {pageData.Publish_Date && (
                                <Text
                                    fontSize="TerminusPublishedDate"
                                    themeKey="textSecondary"
                                >
                                    {`Published: ${new Date(`${pageData.Publish_Date}T00:00:00`).toLocaleDateString()}`}
                                </Text>
                            )}
                        </div>
                    </div>
                )}

                <div
                    css={css({
                        alignItems: "center",
                        display: "flex",
                        gap: Spacing["spacing-4"],
                        justifyContent: "space-between",
                    })}
                >
                    {topics?.length && (
                        <TerminusTopicChipList topics={topics} />
                    )}

                    <Link
                        isUppercase
                        className={css({
                            alignItems: "center",
                            display: "flex",
                            gap: Spacing["spacing-1"],
                        })}
                        fontFamily="mono"
                        fontSize="TerminusQuestionIcon"
                        href="/terminus#about-terminus"
                        interactionType="hover-color-change"
                        themeKey="linkSecondary"
                    >
                        <Icon
                            color={theme.buttons.buttonPrimary.icon.default}
                            size={16}
                            slug="Question"
                        />

                        <span>About Terminus</span>
                    </Link>
                </div>

                <Divider
                    marginBottom="TerminusArticleTopDividerMarginBottom"
                    marginTop="TerminusArticleTopDividerMarginTop"
                />
            </GridColumn>
        );
    };

    const renderAuthorCard = () => {
        const authors = props.pageData.Authors;
        const topics = props.pageData.Topics;

        return (
            <GridColumn
                columnSpan={{
                    extraSmall: "full",
                    medium: 4,
                }}
                columnStart={{
                    extraSmall: 1,
                    medium: 2,
                }}
            >
                <Divider
                    marginBottom="TerminusArticleBottomDividerMarginBottom"
                    marginTop="TerminusArticleBottomDividerMarginTop"
                />

                <Text isUppercase fontFamily="mono" fontSize="TerminusPubDate">
                    Written by
                </Text>

                {authors?.length && (
                    <TerminusAuthorChip
                        author={authors[0]}
                        className={terminusAuthorChipStyles}
                        showCompanyAndRole={true}
                    />
                )}

                {topics?.length && (
                    <div>
                        <Text
                            isUppercase
                            fontFamily="mono"
                            fontSize="TEXT_075"
                            marginBottom="TerminusAuthorCardTopicsMarginBottom"
                        >
                            Filed Under
                        </Text>

                        <TerminusTopicChipList topics={topics} />
                    </div>
                )}
            </GridColumn>
        );
    };

    const renderTerminusArticle = () => {
        return (
            <>
                {renderTerminusArticleHeader()}

                <SubgridContainer
                    columnSpan={{
                        extraSmall: "full",
                        medium: 6,
                    }}
                >
                    <div css={css({ gridColumn: "1/-1" })}>
                        <ArticleBody
                            {...pageData}
                            textBlockLayout={{
                                columnSpan: {
                                    extraSmall: "full",
                                    large: 4,
                                },
                                columnStart: {
                                    extraSmall: 1,
                                    large: 2,
                                },
                            }}
                        />
                    </div>
                </SubgridContainer>

                {renderAuthorCard()}
            </>
        );
    };

    return (
        <TerminusLayout
            breadcrumbs={{
                ...(currentTopic && {
                    level2: {
                        children: currentTopic.Name,
                        href: `/topic/${currentTopic.Slug}`,
                    },
                }),
                level3: {
                    children: pageData.Title,
                    href: `/terminus/${pageData.Slug}`,
                },
            }}
            leftColumn={{
                article: pageData.Article_Body,
                topics: terminusTopicsData,
            }}
            rightColumn={{
                ad: pageData.Sidebar_Ad ?? terminusGlobalsData.Sidebar_Ad,
            }}
            terminusSlugArticleList={{
                articles: Related_Articles ? Related_Articles : [],
            }}
        >
            <Head>
                <script
                    /*
                     * We need to use `dangerouslySetInnerHTML` here, so React
                     * won't escape special characters.
                     */
                    dangerouslySetInnerHTML={{ __html: structuredData }}
                    type="application/ld+json"
                />
            </Head>

            {renderTerminusArticle()}
        </TerminusLayout>
    );
};

export default Page;
